import { useEffect, useState } from 'react';
import { getEventLifecycleLabel, getEventLifecycleState } from '../domain/eventLifecycle';
import {
  canManageHostSettings,
  canUseHostDangerZone,
  loadHostControlPermission,
  saveHostAccessSettings,
  setHostUploadsLocked,
  transitionHostEventLifecycle,
  updateHostBooleanSetting,
  updateHostEventDetails,
} from '../services/hostControls';
import type {
  WebEvent,
  WebEventExpireAction,
  WebPhotoRemovePolicy,
} from '../types/events';
import type { WebEventPermissionSummary } from '../types/people';
import './host-controls.css';

type HostControlsPanelProps = {
  event: WebEvent;
  onEventUpdated: (event: WebEvent) => void;
  onEventDeleted: (eventId: string) => void;
};

type BooleanSetting = 'is_public' | 'allow_guest_downloads' | 'auto_upload_enabled';

function dateTimeLocalValue(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function isoFromLocalInput(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function normalizeExpireAction(value: string | null | undefined): WebEventExpireAction {
  return value === 'archive' ? 'archive' : 'lock';
}

function normalizePhotoPolicy(value: string | null | undefined): WebPhotoRemovePolicy {
  if (value === 'owner_only' || value === 'owner_coadmin') return value;
  return 'owner_coadmin_and_own';
}

export function HostControlsPanel({ event, onEventUpdated, onEventDeleted }: HostControlsPanelProps) {
  const [permission, setPermission] = useState<WebEventPermissionSummary | null>(null);
  const [permissionLoading, setPermissionLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [name, setName] = useState(event.name);
  const [description, setDescription] = useState(event.description ?? '');
  const [startTime, setStartTime] = useState(dateTimeLocalValue(event.start_time));
  const [endTime, setEndTime] = useState(dateTimeLocalValue(event.end_time));
  const [expiresAt, setExpiresAt] = useState(dateTimeLocalValue(event.expires_at));
  const [expireAction, setExpireAction] = useState<WebEventExpireAction>(normalizeExpireAction(event.expire_action));
  const [photoRemovePolicy, setPhotoRemovePolicy] = useState<WebPhotoRemovePolicy>(normalizePhotoPolicy(event.photo_remove_policy));
  const [deleteArmed, setDeleteArmed] = useState(false);
  const [deleteText, setDeleteText] = useState('');

  useEffect(() => {
    let active = true;
    setPermissionLoading(true);
    loadHostControlPermission(event.id)
      .then((next) => {
        if (active) setPermission(next);
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : 'Unable to load host permissions.');
      })
      .finally(() => {
        if (active) setPermissionLoading(false);
      });
    return () => {
      active = false;
    };
  }, [event.id]);

  useEffect(() => {
    setName(event.name);
    setDescription(event.description ?? '');
    setStartTime(dateTimeLocalValue(event.start_time));
    setEndTime(dateTimeLocalValue(event.end_time));
    setExpiresAt(dateTimeLocalValue(event.expires_at));
    setExpireAction(normalizeExpireAction(event.expire_action));
    setPhotoRemovePolicy(normalizePhotoPolicy(event.photo_remove_policy));
  }, [event]);

  const canManage = canManageHostSettings(permission);
  const canDanger = canUseHostDangerZone(permission);
  const lifecycle = getEventLifecycleState(event);
  const archived = lifecycle === 'archived';

  function applyPatch(patch: Partial<WebEvent>) {
    onEventUpdated({ ...event, ...patch, access_role: event.access_role });
  }

  async function saveDetails() {
    setBusy('details');
    setError(null);
    setNotice(null);
    try {
      const updated = await updateHostEventDetails(event.id, {
        name,
        description: description.trim() || null,
        start_time: isoFromLocalInput(startTime),
        end_time: isoFromLocalInput(endTime),
      });
      applyPatch(updated);
      setNotice('Event details saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save event details.');
    } finally {
      setBusy(null);
    }
  }

  async function toggleBoolean(field: BooleanSetting, value: boolean) {
    setBusy(field);
    setError(null);
    setNotice(null);
    try {
      applyPatch(await updateHostBooleanSetting(event.id, field, value));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update this setting.');
    } finally {
      setBusy(null);
    }
  }

  async function saveAccess() {
    setBusy('access');
    setError(null);
    setNotice(null);
    try {
      const updated = await saveHostAccessSettings(event.id, {
        expires_at: isoFromLocalInput(expiresAt),
        expire_action: expireAction,
        photo_remove_policy: photoRemovePolicy,
      });
      applyPatch(updated);
      setNotice('Access and expiration settings saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save access settings.');
    } finally {
      setBusy(null);
    }
  }

  async function toggleUploads(value: boolean) {
    setBusy('uploads');
    setError(null);
    setNotice(null);
    try {
      applyPatch(await setHostUploadsLocked(event.id, value));
      setNotice(value ? 'New uploads are locked.' : 'Uploads are open again.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to change upload access.');
    } finally {
      setBusy(null);
    }
  }

  async function transition(toState: 'active' | 'archived', reason: string) {
    const action = toState === 'archived' ? 'archive/end this event' : 'restore this event';
    if (!window.confirm(`Are you sure you want to ${action}?`)) return;
    setBusy(`lifecycle-${toState}`);
    setError(null);
    setNotice(null);
    try {
      const updated = await transitionHostEventLifecycle({
        eventId: event.id,
        toState,
        reason,
        source: 'web_host_controls',
      });
      applyPatch(updated);
      setNotice(toState === 'archived' ? 'Event archived. New uploads are disabled.' : 'Event restored to active.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to change the event lifecycle.');
    } finally {
      setBusy(null);
    }
  }

  async function deleteEvent() {
    if (deleteText.trim() !== event.name.trim()) return;
    setBusy('delete');
    setError(null);
    setNotice(null);
    try {
      await transitionHostEventLifecycle({
        eventId: event.id,
        toState: 'deleted',
        reason: 'Host confirmed permanent event deletion from web host controls',
        source: 'web_host_controls_safe_delete',
      });
      onEventDeleted(event.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The event could not be queued for deletion.');
    } finally {
      setBusy(null);
    }
  }

  if (permissionLoading) {
    return <section className="host-panel host-state-card">Loading host permissions…</section>;
  }

  if (!canManage) {
    return (
      <section className="host-panel host-state-card">
        <h2>Host controls unavailable</h2>
        <p>Your event role does not include settings management for this event.</p>
      </section>
    );
  }

  return (
    <section className="host-panel">
      <div className="host-summary-card">
        <div>
          <p className="eyebrow">Host controls</p>
          <h2>{event.name}</h2>
          <p>Settings use the same event record and lifecycle RPC as the mobile app.</p>
        </div>
        <div className="host-status-stack">
          <span className="lifecycle-pill">{getEventLifecycleLabel(event)}</span>
          <span>{event.uploads_locked ? 'Uploads locked' : 'Uploads open'}</span>
        </div>
      </div>

      {error && <p className="error-banner host-feedback">{error}</p>}
      {notice && <p className="host-success">{notice}</p>}

      <div className="host-grid">
        <article className="host-card">
          <div className="host-card-heading">
            <div>
              <h3>Event details</h3>
              <p>Edit web-safe event details. Location coordinates remain unchanged.</p>
            </div>
          </div>
          <label className="host-field">
            <span>Event name</span>
            <input value={name} onChange={(input) => setName(input.target.value)} />
          </label>
          <label className="host-field">
            <span>Description</span>
            <textarea rows={4} value={description} onChange={(input) => setDescription(input.target.value)} />
          </label>
          <div className="host-field-row">
            <label className="host-field">
              <span>Starts</span>
              <input type="datetime-local" value={startTime} onChange={(input) => setStartTime(input.target.value)} />
            </label>
            <label className="host-field">
              <span>Ends</span>
              <input type="datetime-local" value={endTime} onChange={(input) => setEndTime(input.target.value)} />
            </label>
          </div>
          <div className="host-readonly-location">
            <strong>Location</strong>
            <span>{event.location_name || event.location_address || 'Not set'}</span>
            <small>Address editing is deferred until browser geocoding matches mobile behavior.</small>
          </div>
          <button className="primary-button" type="button" onClick={() => void saveDetails()} disabled={busy !== null}>
            {busy === 'details' ? 'Saving…' : 'Save event details'}
          </button>
        </article>

        <article className="host-card">
          <div className="host-card-heading">
            <div>
              <h3>Event settings</h3>
              <p>These flags write to the same event fields used by mobile.</p>
            </div>
          </div>
          <label className="host-toggle-row">
            <div><strong>Public event</strong><span>Allow discovery where supported.</span></div>
            <input type="checkbox" checked={Boolean(event.is_public)} onChange={(input) => void toggleBoolean('is_public', input.target.checked)} disabled={busy !== null} />
          </label>
          <label className="host-toggle-row">
            <div><strong>Guest downloads</strong><span>Allow guests to save event media.</span></div>
            <input type="checkbox" checked={event.allow_guest_downloads !== false} onChange={(input) => void toggleBoolean('allow_guest_downloads', input.target.checked)} disabled={busy !== null} />
          </label>
          <label className="host-toggle-row">
            <div><strong>Auto-upload</strong><span>Enable proximity-based mobile auto-upload behavior.</span></div>
            <input type="checkbox" checked={Boolean(event.auto_upload_enabled)} onChange={(input) => void toggleBoolean('auto_upload_enabled', input.target.checked)} disabled={busy !== null} />
          </label>
        </article>

        <article className="host-card">
          <div className="host-card-heading">
            <div>
              <h3>Access & expiration</h3>
              <p>Match the event expiration and photo-removal policy used on mobile.</p>
            </div>
          </div>
          <label className="host-field">
            <span>Expiration</span>
            <input type="datetime-local" value={expiresAt} onChange={(input) => setExpiresAt(input.target.value)} />
          </label>
          <label className="host-field">
            <span>When expired</span>
            <select value={expireAction} onChange={(input) => setExpireAction(input.target.value as WebEventExpireAction)}>
              <option value="lock">Lock uploads</option>
              <option value="archive">Private archive</option>
            </select>
          </label>
          <label className="host-field">
            <span>Who can remove photos?</span>
            <select value={photoRemovePolicy} onChange={(input) => setPhotoRemovePolicy(input.target.value as WebPhotoRemovePolicy)}>
              <option value="owner_only">Owner only</option>
              <option value="owner_coadmin">Owner + co-admins</option>
              <option value="owner_coadmin_and_own">Admins + uploader’s own photos</option>
            </select>
          </label>
          <button className="primary-button" type="button" onClick={() => void saveAccess()} disabled={busy !== null}>
            {busy === 'access' ? 'Saving…' : 'Save access settings'}
          </button>
        </article>

        <article className="host-card">
          <div className="host-card-heading">
            <div>
              <h3>Upload access</h3>
              <p>Lock uploads without removing gallery access or deleting the event.</p>
            </div>
          </div>
          <label className="host-toggle-row host-toggle-prominent">
            <div><strong>Lock uploads now</strong><span>Guests can view existing media but cannot add new photos or videos.</span></div>
            <input type="checkbox" checked={Boolean(event.uploads_locked)} onChange={(input) => void toggleUploads(input.target.checked)} disabled={busy !== null || archived} />
          </label>
          {archived && <p className="host-inline-note">Archived events remain upload-disabled until restored.</p>}
        </article>
      </div>

      {canDanger && (
        <article className="host-danger-zone">
          <div className="host-card-heading">
            <div>
              <p className="eyebrow">Owner only</p>
              <h3>Lifecycle & danger zone</h3>
              <p>Lifecycle actions use `app_transition_event_lifecycle`; the web client never directly deletes the event row.</p>
            </div>
          </div>

          <div className="host-danger-actions">
            {archived ? (
              <button className="secondary-button" type="button" onClick={() => void transition('active', 'Host restored event from web host controls')} disabled={busy !== null}>
                {busy === 'lifecycle-active' ? 'Restoring…' : 'Restore event'}
              </button>
            ) : (
              <button className="host-warning-button" type="button" onClick={() => void transition('archived', 'Host ended or archived event from web host controls')} disabled={busy !== null}>
                {busy === 'lifecycle-archived' ? 'Archiving…' : 'End / archive event'}
              </button>
            )}
            <button className="host-delete-button" type="button" onClick={() => setDeleteArmed(true)} disabled={busy !== null}>
              Delete event
            </button>
          </div>

          {deleteArmed && (
            <div className="host-delete-confirm">
              <strong>Permanent event deletion</strong>
              <p>This queues the event lifecycle for deletion, including event media and related event data according to the existing backend cleanup process.</p>
              <label className="host-field">
                <span>Type “{event.name}” to confirm</span>
                <input value={deleteText} onChange={(input) => setDeleteText(input.target.value)} autoComplete="off" />
              </label>
              <div className="host-danger-actions">
                <button className="secondary-button" type="button" onClick={() => { setDeleteArmed(false); setDeleteText(''); }} disabled={busy !== null}>Cancel</button>
                <button className="host-delete-button" type="button" onClick={() => void deleteEvent()} disabled={busy !== null || deleteText.trim() !== event.name.trim()}>
                  {busy === 'delete' ? 'Queuing deletion…' : 'Permanently delete event'}
                </button>
              </div>
            </div>
          )}
        </article>
      )}
    </section>
  );
}
